import { config } from 'src/config'

// Promesa en vuelo compartida entre todos los llamadores concurrentes.
// Si 5 componentes llaman isReallyOnline() al mismo tiempo,
// solo se hace UN fetch real y todos reciben el mismo resultado.
let _inflightCheck = null

// Fallback de produccion SOLO si no hay backend configurado disponible.
const PROD_FALLBACK = 'https://graphql.binnacle.mx/'

/**
 * Resuelve el host a sondear: el MISMO backend que la app usa de verdad (config `gql.url`), con
 * produccion como FALLBACK (fix #5.1 del audit).
 *
 * Por que importa: en device el probe estaba HARDCODEADO a produccion. Un telefono con SOLO red LAN
 * al backend Homestead (sin internet hacia graphql.binnacle.mx) recibia SIEMPRE false -> el
 * sync-worker NUNCA drenaba aunque el backend LAN si estuviera accesible. Sondeando el backend
 * CONFIGURADO, un device en LAN obtiene `true` cuando ese backend responde.
 *
 * CORS: el GET va con `mode: 'no-cors'`, asi que un host cross-origin (cualquier backend, incluido el
 * LAN) responde de forma opaca sin requerir cabeceras CORS. Solo nos importa que la red complete el
 * round-trip, no leer el cuerpo. Por eso `no-cors` es seguro aqui (escenario device-LAN).
 */
export function getProbeUrl () {
  try {
    const gql = config('gql.url')
    if (gql && typeof gql === 'string' && /^https?:\/\//i.test(gql)) {
      // Normaliza a una sola barra final (gql.url puede o no traerla).
      return gql.replace(/\/+$/, '') + '/'
    }
  } catch {
    // cae al fallback
  }
  return PROD_FALLBACK
}

// Un solo intento de probe no-cors con timeout propio.
async function probeOnce (url, timeout) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  try {
    // RED-DE-SEGURIDAD (repro en device: el sync se quedó pegado ~minutos hasta reiniciar la app): en
    // WebView el abort de un fetch `no-cors` a veces NO rechaza la promesa -> `await fetch` colgaba PARA
    // SIEMPRE, colgando isReallyOnline y —por el Web Lock (ifAvailable) del tick del sync-worker— TODO
    // el drain periódico (incluido el nudge del cierre). Se corre el fetch contra un timeout DURO:
    // probeOnce SIEMPRE resuelve en <= timeout+250ms aunque el fetch nunca conteste.
    const fetchProbe = fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store', signal: controller.signal })
      .then(() => true, () => false)
    const hardTimeout = new Promise(resolve => setTimeout(() => resolve(false), timeout + 250))
    return await Promise.race([fetchProbe, hardTimeout])
  } finally {
    clearTimeout(timer)
  }
}

// REGRESION CAZADA POR LA MATRIZ (2026-06-10): con timeout=3000 el backend LAN (que en frio tarda
// 5-12s en responder el root) abortaba SIEMPRE -> isReallyOnline()=false -> el tick jamas drenaba.
// Fix: timeout 15s (el probe es no-cors barato y corre como mucho 1 vez por tick) + si el backend
// configurado no responde, se intenta el FALLBACK de produccion antes de declarar offline (cubre
// tanto device-solo-LAN como LAN-lenta-con-internet).
export async function isReallyOnline (timeout = 15000) {
  if (!navigator.onLine) return false

  if (_inflightCheck) return _inflightCheck

  const url = getProbeUrl()

  _inflightCheck = (async () => {
    try {
      if (await probeOnce(url, timeout)) return true
      if (url !== PROD_FALLBACK && await probeOnce(PROD_FALLBACK, timeout)) return true
      return false
    } catch {
      return false
    }
  })().finally(() => {
    // Liberar para que el próximo ciclo de respuestas haga un check fresco
    _inflightCheck = null
  })

  return _inflightCheck
}

// Cota dura para llamadores de UI (PR review F9): isReallyOnline puede tardar hasta 15s por probe
// (+ fallback a produccion) — correcto para el sync-worker en background (backend LAN frio tarda
// 5-12s), pero un await en un flujo de UI (Finalizar, setAnswer, dialogo de firmas) congelaba la
// pantalla hasta ~30s en portales cautivos/red medio-muerta. Si el probe robusto no contesta en
// maxWaitMs, se asume OFFLINE: para estos flujos eso solo significa "encolar/cachear/omitir el
// dialogo online", que es seguro y drena despues. El probe de fondo SIGUE corriendo (promesa en
// vuelo compartida) y calienta el resultado para el siguiente caller.
const UI_MAX_WAIT_MS = 4000

export async function isReallyOnlineUi (maxWaitMs = UI_MAX_WAIT_MS) {
  let timer
  const capped = new Promise((resolve) => { timer = setTimeout(() => resolve(false), maxWaitMs) })
  try {
    return await Promise.race([isReallyOnline(), capped])
  } finally {
    clearTimeout(timer)
  }
}

// Promesa en vuelo compartida del probe SIN guarda (mismo motivo que `_inflightCheck`).
let _inflightReach = null

/**
 * Igual que `isReallyOnlineUi` pero SIN la guarda `navigator.onLine`: sondea la red DE VERDAD.
 *
 * POR QUE EXISTE (TKT-2195): en el WebView de Android `navigator.onLine` se queda PEGADO en false
 * cuando la transicion de red ocurre con la app en background (el NetworkChangeNotifier de Chromium
 * se pierde el broadcast y ya no emite el evento 'online'). El flag no se cura solo: solo lo destraba
 * otra transicion real de red — que es exactamente el ritual "modo avion + reiniciar" que el cliente
 * tuvo que hacer para que reapareciera FINALIZAR. Por eso un gate de UI no puede confiar en ese flag
 * como unica fuente: hay que poder preguntarle a la RED, no al navegador.
 *
 * NO reemplaza a `isReallyOnline` en el sync-worker (ese SI respeta navigator.onLine, y ahi esta
 * bien: el worker corre en background y el probe barato de mas no aporta). Esto es solo para el
 * autodiagnostico del flag global de conectividad (services/online.js).
 */
export async function isReachableUi (maxWaitMs = UI_MAX_WAIT_MS) {
  if (_inflightReach) return _inflightReach

  const url = getProbeUrl()
  const probe = (async () => {
    if (await probeOnce(url, maxWaitMs)) return true
    if (url !== PROD_FALLBACK && await probeOnce(PROD_FALLBACK, maxWaitMs)) return true
    return false
  })()

  // Cota dura: probeOnce ya resuelve solo, pero el fallback encadena dos probes. Un gate de UI no
  // puede quedarse esperando el doble del presupuesto.
  let timer
  const capped = new Promise((resolve) => { timer = setTimeout(() => resolve(false), maxWaitMs + 300) })

  _inflightReach = Promise.race([probe, capped]).finally(() => {
    clearTimeout(timer)
    _inflightReach = null
  })

  return _inflightReach
}

export async function isReallyOffline () {
  const on = await isReallyOnline()
  return !on
}
